import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { superadminAPI, analyticsV2API, connectorVotesAPI } from '@/lib/api';
import { formatCellValue } from '@/lib/utils';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Building2,
  Plus,
  Users,
  FileText,
  ExternalLink,
  Shield,
  ArrowLeft,
  X,
  Trash2,
  Power,
  PowerOff,
  MoreHorizontal,
  BarChart3,
  Clock,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp,
  Settings,
  Calendar,
  LogOut,
  Monitor,
  Smartphone,
  Coins,
  Zap,
  Tags,
  Save,
  UserPlus,
  Loader2,
  Sparkles,
  RefreshCw,
  Search,
  Fingerprint,
  Mail,
  Activity,
  Download,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PageShell } from '@/components/PageShell';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTablePagination } from '@/components/DataTableControls';
import { TenantIcon } from '@/components/TenantIcon';

import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui/radio-group';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Real guard lives server-side (backend/db/tenant_schema.py:BREAK_GLASS_SUPERADMIN_EMAIL);
// mirrored here only to disable the destructive actions in the UI.
const BREAK_GLASS_SUPERADMIN_EMAIL = 'root@vistry.ai';

export default function TenantManagementPage() {
  const { user, isSuperadmin, switchTenant, tenant: activeTenant, refreshTenant } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [superadmins, setSuperadmins] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [addSuperadminDialogOpen, setAddSuperadminDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [selectedTenantAnalytics, _setSelectedTenantAnalytics] = useState(null);
  const [analyticsLoading, _setAnalyticsLoading] = useState(false);
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantPlanFilter, setTenantPlanFilter] = useState('all');
  const [tenantStatusFilter, setTenantStatusFilter] = useState('all');
  const [tenantPage, setTenantPage] = useState(1);
  const [tenantPageSize, setTenantPageSize] = useState(10);
  const [createPlanOptions, setCreatePlanOptions] = useState([]);
  
  // Delete/Deactivate dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [convertToPaidDialogOpen, setConvertToPaidDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  
  // Session management
  const [sessionsDialogOpen, setSessionsDialogOpen] = useState(false);
  const [tenantSessions, setTenantSessions] = useState([]);
  const [sessionsLoading, _setSessionsLoading] = useState(false);
  const [revokingSession, setRevokingSession] = useState(null);
  
  // Convert to paid form
  const [convertMethod, setConvertMethod] = useState('already_paid');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [convertLoading, setConvertLoading] = useState(false);
  
  // Token usage management
  const [tokenUsageDialogOpen, setTokenUsageDialogOpen] = useState(false);
  const [tenantTokenUsage, setTenantTokenUsage] = useState(null);
  const [tokenUsageLoading, setTokenUsageLoading] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [resettingUsage, setResettingUsage] = useState(false);
  const [limitsDialogOpen, setLimitsDialogOpen] = useState(false);
  const [tenantLimits, setTenantLimits] = useState(null);
  const [limitDraft, setLimitDraft] = useState({});
  const [limitsLoading, setLimitsLoading] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  const [ConfirmEl, confirm] = useConfirm();
  
  // Access requests management
  const [accessRequests, setAccessRequests] = useState([]);
  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [selectedRole, setSelectedRole] = useState('admin');

  // Signup activity / attribution (see docs/eva-insights-signup-attribution-lifecycle-ticket.md)
  const [pendingSignups, setPendingSignups] = useState([]);
  const [signupActivity, setSignupActivity] = useState([]);
  const [resendingEmail, setResendingEmail] = useState(null);
  const [activityEventFilter, setActivityEventFilter] = useState('');
  const [activityCampaignFilter, setActivityCampaignFilter] = useState('');
  
  // Create tenant form
  const [newTenant, setNewTenant] = useState({
    shortname: '',
    display_name: '',
    email_domains: '',
    primary_color: '#6366f1',
    secondary_color: '#818cf8',
    initial_admin_email: '',
    pricing_tier: '',
  });
  
  // Add superadmin form
  const [newSuperadmin, setNewSuperadmin] = useState({
    email: '',
    name: '',
  });
  
  // Superadmin invitations
  const [invitations, setInvitations] = useState([]);
  const [invitationLoading, setInvitationLoading] = useState(false);
  
  // Trial config form - initialized to null, loaded from API
  const [trialConfig, setTrialConfig] = useState(null);

  // Model options for dropdowns
  const MODEL_OPTIONS = {
    fast: [
      { value: 'claude-sonnet-4-6', label: 'Eva Smart' },
      { value: 'claude-haiku-4-5-20251001', label: 'Eva Fast' },
    ],
    thinking: [
      { value: 'claude-opus-4-6', label: 'Eva Deep' },
      { value: 'claude-sonnet-4-6', label: 'Eva Smart' },
    ],
  };
  
  // DuckDB Analytics data
  const [tenantUsageAnalytics, setTenantUsageAnalytics] = useState([]);

  // Connector votes (global demand)
  const [connectorVotes, setConnectorVotes] = useState([]);
  const [, setUserVoted] = useState([]);

  // Default settings for new tenants
  const [defaultSettings, setDefaultSettings] = useState(null);
  const [savingDefaults, setSavingDefaults] = useState(false);

  useEffect(() => {
    if (!isSuperadmin) {
      navigate('/overview');
      return;
    }
    loadData();
    loadConnectorVotes();
    loadDefaultSettings();
  }, [isSuperadmin, navigate]);

  const loadConnectorVotes = async () => {
    try {
      const data = await connectorVotesAPI.getVotes();
      setConnectorVotes(data.votes || []);
      setUserVoted(data.user_voted || []);
    } catch (_e) {
      // non-blocking
    }
  };

  const loadDefaultSettings = async () => {
    try {
      const data = await superadminAPI.getDefaultSettings();
      setDefaultSettings(data);
    } catch (_e) {
      // non-blocking
    }
  };

  const saveDefaultSettings = async () => {
    setSavingDefaults(true);
    try {
      await superadminAPI.updateDefaultSettings(defaultSettings);
      toast.success('Default settings saved');
    } catch (_e) {
      toast.error('Failed to save defaults');
    } finally {
      setSavingDefaults(false);
    }
  };

  const loadData = async () => {
    try {
      const [summaryData, superadminsData, usageData, invitationsData, accessRequestsData, pricingTiersData, pendingSignupsData, signupActivityData] = await Promise.allSettled([
        superadminAPI.getTenantsSummary(),
        superadminAPI.listSuperadmins(),
        analyticsV2API.getTenantUsage(),
        superadminAPI.listSuperadminInvitations(),
        superadminAPI.listAllAccessRequests(),
        superadminAPI.getPricingTiers(),
        superadminAPI.listPendingSignups(),
        superadminAPI.listSignupActivity(),
      ]);
      if (summaryData.status === 'fulfilled') {
        setTenants(summaryData.value.tenants || []);
        setSummary(summaryData.value.summary || null);
      }
      if (superadminsData.status === 'fulfilled') setSuperadmins(superadminsData.value.superadmins || []);
      if (usageData.status === 'fulfilled') setTenantUsageAnalytics(usageData.value.tenants || []);
      if (invitationsData.status === 'fulfilled') setInvitations(invitationsData.value.invitations || []);
      if (accessRequestsData.status === 'fulfilled') setAccessRequests(accessRequestsData.value.requests || []);
      if (pricingTiersData.status === 'fulfilled') {
        setCreatePlanOptions(pricingTiersData.value.tiers || []);
      }
      if (pendingSignupsData.status === 'fulfilled') setPendingSignups(pendingSignupsData.value.pending_signups || []);
      if (signupActivityData.status === 'fulfilled') setSignupActivity(signupActivityData.value.events || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSignupActivity = async (filters = {}) => {
    try {
      const data = await superadminAPI.listSignupActivity(filters);
      setSignupActivity(data.events || []);
    } catch (error) {
      console.error('Failed to load signup activity:', error);
    }
  };

  const handleResendVerification = async (email) => {
    setResendingEmail(email);
    try {
      await superadminAPI.resendVerification(email);
      toast.success('Verification email resent', { description: email });
    } catch (error) {
      console.error('Failed to resend verification:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setResendingEmail(null);
    }
  };

  const exportSignupActivityCsv = () => {
    const headers = ['created_at', 'event_name', 'event_status', 'email', 'tenant_display_name', 'utm_source', 'utm_medium', 'utm_campaign', 'landing_host', 'cta'];
    const rows = signupActivity.map((e) => headers.map((h) => JSON.stringify(e[h] ?? '')).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `signup-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  
  const handleReviewAccessRequest = async (requestId, action) => {
    setReviewingRequest(requestId);
    try {
      await superadminAPI.reviewAccessRequest(requestId, {
        action: action,
        role: selectedRole,
      });
      toast.success(`Access request ${action === 'approve' ? 'approved' : 'declined'}`);
      // Reload access requests
      const accessRequestsData = await superadminAPI.listAllAccessRequests().catch(() => ({ requests: [] }));
      setAccessRequests(accessRequestsData.requests || []);
    } catch (error) {
      console.error('Failed to review access request:', error);
      toast.error(error.response?.data?.detail || 'Failed to review access request');
    } finally {
      setReviewingRequest(null);
    }
  };

  const handleCreateTenant = async () => {
    if (!newTenant.shortname || !newTenant.display_name || !newTenant.email_domains) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!newTenant.pricing_tier) {
      toast.error('Please select a plan');
      return;
    }

    try {
      const payload = { ...newTenant };
      if (!payload.initial_admin_email) {
        delete payload.initial_admin_email;
      }
      const createdTenant = await superadminAPI.createTenant(payload);
      toast.success(`Tenant "${newTenant.display_name}" created successfully`);
      window.dispatchEvent(new CustomEvent('eva:tenants-updated', {
        detail: { tenant: createdTenant?.tenant || createdTenant },
      }));
      setCreateDialogOpen(false);
      setNewTenant({
        shortname: '',
        display_name: '',
        email_domains: '',
        primary_color: '#6366f1',
        secondary_color: '#818cf8',
        initial_admin_email: '',
        pricing_tier: '',
      });
      loadData();
    } catch (error) {
      console.error('Failed to create tenant:', error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : detail;
      toast.error(msg || 'Failed to create tenant');
    }
  };

  const handleAddSuperadmin = async () => {
    if (!newSuperadmin.email) {
      toast.error('Please enter an email address');
      return;
    }

    setInvitationLoading(true);
    try {
      const result = await superadminAPI.sendSuperadminInvitation(newSuperadmin.email, newSuperadmin.name);
      if (result.email_sent) {
        toast.success(`Invitation sent to ${newSuperadmin.email}`);
      } else {
        toast.success(`Invitation created for ${newSuperadmin.email}. Check server logs for the invitation link.`);
      }
      setAddSuperadminDialogOpen(false);
      setNewSuperadmin({ email: '', name: '' });
      loadData();
    } catch (error) {
      console.error('Failed to send invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInvitationLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await superadminAPI.cancelSuperadminInvitation(invitationId);
      toast.success('Invitation cancelled');
      loadData();
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel invitation');
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      const result = await superadminAPI.resendSuperadminInvitation(invitationId);
      if (result.email_sent) {
        toast.success('Invitation resent');
      } else {
        toast.success('Invitation resent. Check server logs for the link.');
      }
      loadData();
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const handleDeactivateSuperadmin = async (email) => {
    const ok = await confirm({
      title: 'Deactivate Superadmin',
      description: `Deactivate ${email}? They will immediately lose superadmin access. This can be undone later with Reactivate.`,
      confirmLabel: 'Deactivate',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      await superadminAPI.deactivateSuperadmin(email);
      toast.success('Superadmin deactivated');
      loadData();
    } catch (error) {
      console.error('Failed to deactivate superadmin:', error);
      toast.error(error.response?.data?.detail || 'Failed to deactivate superadmin');
    }
  };

  const handleReactivateSuperadmin = async (email) => {
    try {
      await superadminAPI.reactivateSuperadmin(email);
      toast.success('Superadmin reactivated');
      loadData();
    } catch (error) {
      console.error('Failed to reactivate superadmin:', error);
      toast.error(error.response?.data?.detail || 'Failed to reactivate superadmin');
    }
  };

  const handleHardDeleteSuperadmin = async (email) => {
    const ok = await confirm({
      title: 'Permanently Remove Superadmin',
      description: `This permanently deletes ${email}'s superadmin record and their Vistry login account. This cannot be undone.`,
      confirmLabel: 'Delete Permanently',
      variant: 'delete',
    });
    if (!ok) return;

    try {
      await superadminAPI.hardDeleteSuperadmin(email);
      toast.success('Superadmin permanently removed');
      loadData();
    } catch (error) {
      console.error('Failed to remove superadmin:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove superadmin');
    }
  };

  const handleViewTenant = async (tenantId) => {
    try {
      await switchTenant(tenantId);
      window.location.href = '/overview';
    } catch (error) {
      console.error('Failed to switch tenant:', error);
      toast.error('Failed to switch tenant');
    }
  };

  const handleDeactivateTenant = async () => {
    if (!selectedTenant) return;
    try {
      if (selectedTenant.status === 'active') {
        await superadminAPI.deactivateTenant(selectedTenant.id);
        toast.success('Tenant deactivated');
      } else {
        await superadminAPI.activateTenant(selectedTenant.id);
        toast.success('Tenant activated');
      }
      setDeactivateDialogOpen(false);
      setSelectedTenant(null);
      loadData();
    } catch (error) {
      console.error('Failed to update tenant status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update tenant status');
    }
  };

  const handleDeleteTenant = async () => {
    if (!selectedTenant) return;
    try {
      await superadminAPI.deleteTenant(selectedTenant.id);
      toast.success('Tenant deleted');
      setDeleteDialogOpen(false);
      setSelectedTenant(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete tenant');
    }
  };

  const handleUpdateTrial = async () => {
    if (!selectedTenant) return;
    try {
      await superadminAPI.updateTenantTrial(selectedTenant.id, trialConfig);
      toast.success('Trial configuration applied');
      setTrialDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to update trial:', error);
      toast.error(error.response?.data?.detail || 'Failed to update trial');
    }
  };

  const openConvertToPaidDialog = (tenant) => {
    setSelectedTenant(tenant);
    setConvertMethod('already_paid');
    setBillingPeriod('monthly');
    setConvertToPaidDialogOpen(true);
  };

  const handleConvertToPaid = async () => {
    if (!selectedTenant?.id) return;
    
    setConvertLoading(true);
    try {
      const response = await superadminAPI.convertTenantToPaid(selectedTenant.id, {
        method: convertMethod,
        billing_period: billingPeriod,
      });
      
      if (convertMethod === 'generate_invoice') {
        toast.success(`Invoice created for $${response.invoice?.amount || 'N/A'}. Tenant will be notified.`);
      } else {
        toast.success('Tenant converted to paid subscription');
      }
      
      setConvertToPaidDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to convert tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert tenant');
    } finally {
      setConvertLoading(false);
    }
  };

  const handleExtendTrial = async (tenantId, days = 14) => {
    try {
      await superadminAPI.updateTenantSubscription(tenantId, {
        status: 'trial',
        trial_extension_days: days
      });
      toast.success(`Trial extended by ${days} days`);
      loadData();
      // EIP-169: loadData only refreshes the tenant list. When a superadmin is
      // viewing this tenant, the DashboardLayout trial banner is driven by
      // useAuth().tenant — refresh it so it reflects the new end date.
      if (tenantId === activeTenant?.tenant_id) {
        await refreshTenant();
      }
    } catch (error) {
      console.error('Failed to extend trial:', error);
      toast.error(error.response?.data?.detail || 'Failed to extend trial');
    }
  };

  // Session management functions
  const handleRevokeSession = async (sessionId) => {
    if (!selectedTenant) return;
    setRevokingSession(sessionId);
    
    try {
      await superadminAPI.revokeSession(selectedTenant.id, sessionId);
      toast.success('Session revoked - user has been logged out');
      // Refresh sessions list
      const data = await superadminAPI.getTenantSessions(selectedTenant.id);
      setTenantSessions(data.sessions || []);
    } catch (error) {
      console.error('Failed to revoke session:', error);
      toast.error(error.response?.data?.detail || 'Failed to revoke session');
    } finally {
      setRevokingSession(null);
    }
  };

  const handleRevokeAllSessions = async () => {
    if (!selectedTenant) return;
    const ok = await confirm({ title: 'Force Logout All Users', description: `Are you sure you want to log out ALL users from ${selectedTenant.display_name}? This will force everyone to log in again.`, confirmLabel: 'Log Out All', variant: 'warning' });
    if (!ok) return;
    
    try {
      const result = await superadminAPI.revokeAllSessions(selectedTenant.id);
      toast.success(`${result.sessions_revoked} sessions revoked - all users have been logged out`);
      setTenantSessions([]);
    } catch (error) {
      console.error('Failed to revoke all sessions:', error);
      toast.error(error.response?.data?.detail || 'Failed to revoke all sessions');
    }
  };

  // Token usage management functions
  const _handleViewTokenUsage = async (tenant) => {
    setSelectedTenant(tenant);
    setTokenUsageLoading(true);
    setTokenUsageDialogOpen(true);
    
    try {
      const [usageData, plansData] = await Promise.all([
        superadminAPI.getTenantTokenUsage(tenant.id),
        superadminAPI.getAvailablePlans()
      ]);
      setTenantTokenUsage(usageData);
      setAvailablePlans(plansData.plans || []);
      setSelectedPlan(usageData.plan?.type || 'trial');
    } catch (error) {
      console.error('Failed to load token usage:', error);
      toast.error('Failed to load token usage data');
      setTenantTokenUsage(null);
    } finally {
      setTokenUsageLoading(false);
    }
  };

  const handleUpdatePlan = async () => {
    if (!selectedTenant || !selectedPlan) return;
    setUpdatingPlan(true);
    
    try {
      const result = await superadminAPI.updateTenantPlan(selectedTenant.id, selectedPlan);
      setTenantTokenUsage(result);
      toast.success(`Plan updated to ${selectedPlan}`);
    } catch (error) {
      console.error('Failed to update plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    } finally {
      setUpdatingPlan(false);
    }
  };

  const handleResetUsage = async () => {
    if (!selectedTenant) return;
    const ok = await confirm({ title: 'Reset Usage Counters', description: `Reset usage counters for ${selectedTenant.display_name}? This cannot be undone.`, confirmLabel: 'Reset', variant: 'warning' });
    if (!ok) return;
    setResettingUsage(true);
    
    try {
      await superadminAPI.resetTenantUsage(selectedTenant.id);
      // Refresh usage data
      const usageData = await superadminAPI.getTenantTokenUsage(selectedTenant.id);
      setTenantTokenUsage(usageData);
      toast.success('Usage counters reset');
    } catch (error) {
      console.error('Failed to reset usage:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset usage');
    } finally {
      setResettingUsage(false);
    }
  };

  const formatTokens = (tokens) => {
    if (!tokens) return '0';
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
    return tokens.toString();
  };

  const formatCost = (cost) => {
    if (!cost) return '$0.00';
    return `$${cost.toFixed(4)}`;
  };

  const limitFields = [
    { key: 'employee_limit', label: 'Employee seats', type: 'number' },
    { key: 'content_team_limit', label: 'Content team seats', type: 'number' },
    { key: 'document_limit', label: 'Documents', type: 'number' },
    { key: 'topics_limit', label: 'Topics', type: 'number' },
    { key: 'integrations_limit', label: 'Connectors', type: 'number' },
    { key: 'monthly_token_limit', label: 'Total monthly usage pool', type: 'number' },
    { key: 'monthly_cost_limit_usd', label: 'Monthly cost limit', type: 'decimal' },
    { key: 'tokens_per_seat_month', label: 'Eva People usage per seat', type: 'number' },
    { key: 'user_daily_capacity_pct', label: 'Daily Eva People rate limit', type: 'decimal' },
    { key: 'user_weekly_capacity_pct', label: 'Weekly Eva People rate limit', type: 'decimal' },
    { key: 'user_monthly_capacity_pct', label: 'Monthly Eva People rate limit', type: 'decimal' },
    { key: 'max_chunks_per_document', label: 'Pages per document', type: 'number' },
  ];

  const formatLimit = (value) => {
    if (value === null || value === undefined || value === '') return 'Not configured';
    if (typeof value === 'number') return value.toLocaleString();
    return value;
  };

  const formatLimitSource = (source) => {
    const labels = {
      tenant_override: 'Tenant override',
      trial_config: 'Trial config',
      trial_override: 'Trial config',
	      plan_default: 'Plan default',
	      not_configured: 'Not configured',
	      custom_not_configured: 'Needs setup',
	    };
    return labels[source] || 'Default';
  };

  const openLimitsDialog = async (tenant) => {
    setSelectedTenant(tenant);
    setLimitsDialogOpen(true);
    setLimitsLoading(true);
    setTenantLimits(null);
    try {
      const data = await superadminAPI.getTenantLimits(tenant.id);
      setTenantLimits(data);
      const draft = {};
      limitFields.forEach(({ key }) => {
        draft[key] = data.overrides?.[key] ?? '';
      });
      setLimitDraft(draft);
    } catch (error) {
      console.error('Failed to load tenant limits:', error);
      toast.error(error.response?.data?.detail || 'Failed to load tenant limits');
    } finally {
      setLimitsLoading(false);
    }
  };

  const handleSaveLimits = async () => {
    if (!selectedTenant) return;
    const payload = {};
    limitFields.forEach(({ key }) => {
      const value = limitDraft[key];
      if (value === '' || value === null || value === undefined) return;
      payload[key] = value;
    });

    setSavingLimits(true);
    try {
      const data = await superadminAPI.updateTenantLimits(selectedTenant.id, payload);
      setTenantLimits(data);
      toast.success('Tenant limits updated');
      loadData();
      if (tokenUsageDialogOpen && selectedTenant?.id) {
        superadminAPI.getTenantTokenUsage(selectedTenant.id).then(setTenantTokenUsage).catch(() => {});
      }
    } catch (error) {
      console.error('Failed to save tenant limits:', error);
      toast.error(error.response?.data?.detail || 'Failed to save tenant limits');
    } finally {
      setSavingLimits(false);
    }
  };

  const getPlanBadgeColor = (plan) => {
    const colors = {
      trial: 'bg-warning/10 text-warning',
      basic: 'bg-blue-500/10 text-blue-500',
      pro: 'bg-primary/10 text-primary',
      enterprise: 'bg-success/10 text-success',
    };
    return colors[plan] || 'bg-secondary text-muted-foreground';
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Single source of truth for tenant state -- consolidates what used to be four separate
  // renders of the same two underlying fields (tenant.status, tenant.subscription_status)
  // spread across a "Plan" column badge+badge and a separate "Status" column badge+text.
  // Priority order matters: trial_expired/terminated/suspended/cancelled/pending_payment are
  // all more specific than the generic "Inactive" fallback, and within_limits only matters
  // once a tenant is otherwise active (an inactive-for-nonpayment tenant isn't "needs
  // attention", it's "payment required").
  const getTenantStatusInfo = (tenant) => {
    const status = tenant.subscription_status || 'trial';
    if (tenant.trial_expired) {
      return { badge: <Badge className="bg-destructive/10 text-destructive">Trial expired</Badge> };
    }
    if (status === 'terminated') {
      return { badge: <Badge className="bg-destructive/10 text-destructive">Terminated</Badge> };
    }
    if (status === 'suspended') {
      return { badge: <Badge className="bg-destructive/10 text-destructive">Suspended</Badge> };
    }
    if (status === 'cancelled') {
      return { badge: <Badge className="bg-muted text-muted-foreground">Cancelled</Badge> };
    }
    if (status === 'pending_payment') {
      return { badge: <Badge className="bg-warning/10 text-warning">Payment required</Badge> };
    }
    if (tenant.status !== 'active') {
      return { badge: <Badge variant="outline">Inactive</Badge> };
    }
    if (tenant.within_limits === false) {
      return { badge: <Badge className="bg-warning/10 text-warning">Needs attention</Badge> };
    }
    if (status === 'trial') {
      return {
        badge: <Badge className="bg-warning/10 text-warning">Trial</Badge>,
        secondary: tenant.days_remaining > 0 ? `${tenant.days_remaining} days left` : null,
      };
    }
    return { badge: <Badge className="bg-success/10 text-success">Active</Badge> };
  };

  const getTenantPlanLabel = (tenant) => {
    if (tenant.pricing_tier) return tenant.pricing_tier;
    if (tenant.plan_name) return tenant.plan_name;
    if (tenant.plan_type) return tenant.plan_type;
    if (tenant.subscription_status === 'trial') return 'Trial';
    return 'Not configured';
  };

  const getTenantPlanKey = (tenant) => getTenantPlanLabel(tenant).toLowerCase();

  // Seat-based list-price estimate, NOT actual MRR (which is invoice-derived and
  // shown on the tenant detail page). Labeled "Est. monthly" to avoid conflating
  // the two figures.
  const getTenantMonthlyEstimateLabel = (tenant) => {
    const estimate = tenant.monthly_estimate;
    if (estimate === null || estimate === undefined) return 'Est. monthly —';
    if (Number(estimate) === 0 && tenant.subscription_status === 'active') return 'Est. monthly —';
    return `Est. monthly $${Number(estimate || 0).toLocaleString()}`;
  };

  const planFilterOptions = Array.from(
    new Set(tenants.map((tenant) => getTenantPlanLabel(tenant)).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const statusFilterOptions = Array.from(
    new Set(tenants.map((tenant) => tenant.status || 'unknown').filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const filteredTenants = tenants.filter((tenant) => {
    const query = tenantSearch.trim().toLowerCase();
    const matchesSearch = !query
      || tenant.display_name?.toLowerCase().includes(query)
      || tenant.shortname?.toLowerCase().includes(query)
      || tenant.email_domains?.toLowerCase().includes(query);
    const matchesPlan = tenantPlanFilter === 'all' || getTenantPlanKey(tenant) === tenantPlanFilter;
    const matchesStatus = tenantStatusFilter === 'all' || tenant.status === tenantStatusFilter;
    return matchesSearch && matchesPlan && matchesStatus;
  });

  const tenantTotalPages = Math.max(Math.ceil(filteredTenants.length / tenantPageSize), 1);
  const normalizedTenantPage = Math.min(tenantPage, tenantTotalPages);
  const pagedTenants = filteredTenants.slice(
    (normalizedTenantPage - 1) * tenantPageSize,
    normalizedTenantPage * tenantPageSize
  );
  const createPaidPlanOptions = createPlanOptions.filter((plan) => {
    const planName = plan.name?.toLowerCase();
    return plan.is_active !== false && planName !== 'trial' && planName !== 'enterprise';
  });
  const selectedCreatePlan = createPaidPlanOptions.find(
    (plan) => plan.name?.toLowerCase() === newTenant.pricing_tier?.toLowerCase()
  );
  const selectedCreatePlanKey = (newTenant.pricing_tier || '').toLowerCase();
  const createTenantIsTrial = selectedCreatePlanKey === 'trial';
  const selectedCreatePlanIsEnterprise = selectedCreatePlanKey === 'enterprise';
  const createTenantWillBeInactive = Boolean(newTenant.pricing_tier) && !createTenantIsTrial;
  const createTenantPlanHelp = !newTenant.pricing_tier
    ? 'Choose Trial, an active paid plan, or Enterprise. Scale is hidden while inactive.'
    : createTenantIsTrial
      ? 'Trial tenants start active with default trial limits and can receive an initial admin invitation.'
      : selectedCreatePlanIsEnterprise
        ? 'Enterprise tenants are created inactive until custom entitlements are configured. Invite an admin now — they can complete setup and payment once they accept.'
        : `${selectedCreatePlan?.name || newTenant.pricing_tier} tenants are created pending payment. Invite an admin now — they can complete payment from Billing & Subscription once they accept.`;

  useEffect(() => {
    setTenantPage(1);
  }, [tenantSearch, tenantPlanFilter, tenantStatusFilter, tenantPageSize]);

  if (!isSuperadmin) {
    return null;
  }

  return (
    <PageShell maxWidth="7xl" data-testid="tenant-management-page">
      {ConfirmEl}
      {/* Header with Superadmin Badge */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/overview')}
            className="mr-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                Tenant Management
              </h1>
              <Badge className="bg-primary text-primary-foreground">
                <Shield className="h-3 w-3 mr-1" />
                Superadmin Only
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Manage all tenants, trials, and platform superadmins
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {loading && !summary ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5" data-testid="tenant-summary-loading">
          {[1, 2, 3, 4, 5].map((item) => (
            <Card key={item}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-8 rounded-lg" />
                  <div className="space-y-2">
                    <Skeleton className="h-7 w-12" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : summary && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Building2 className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{summary.total}</p>
                  <p className="text-xs text-muted-foreground">Total Tenants</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="h-8 w-8 text-warning" />
                <div>
                  <p className="text-2xl font-bold">{summary.active_trials}</p>
                  <p className="text-xs text-muted-foreground">Active Trials</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-8 w-8 text-destructive" />
                <div>
                  <p className="text-2xl font-bold">{summary.expired_trials}</p>
                  <p className="text-xs text-muted-foreground">Expired Trials</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-8 w-8 text-success" />
                <div>
                  <p className="text-2xl font-bold">{summary.paid_subscriptions}</p>
                  <p className="text-xs text-muted-foreground">Paid</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="h-8 w-8 text-success" />
                <div>
                  <p className="text-2xl font-bold">${summary.potential_mrr?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Potential MRR</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for Tenants and Superadmins */}
      <Tabs defaultValue="tenants" className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="tenants" data-testid="tenants-tab">
              <Building2 className="h-4 w-4 mr-2" />
              Tenants
            </TabsTrigger>
            <TabsTrigger value="superadmins" data-testid="superadmins-tab">
              <Shield className="h-4 w-4 mr-2" />
              Superadmins
            </TabsTrigger>
            <TabsTrigger value="access-requests" data-testid="access-requests-tab">
              <UserPlus className="h-4 w-4 mr-2" />
              Access Requests
              {accessRequests.filter(r => r.status === 'pending').length > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 min-w-[20px] px-1.5">
                  {accessRequests.filter(r => r.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="signup-activity" data-testid="signup-activity-tab">
              <Activity className="h-4 w-4 mr-2" />
              Signup Activity
              {pendingSignups.filter((p) => !p.email_verified).length > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 min-w-[20px] px-1.5">
                  {pendingSignups.filter((p) => !p.email_verified).length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="connector-demand" data-testid="connector-demand-tab">
              <TrendingUp className="h-4 w-4 mr-2" />
              Connector Demand
            </TabsTrigger>
            <TabsTrigger value="defaults" data-testid="defaults-tab">
              <Settings className="h-4 w-4 mr-2" />
              Defaults
            </TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/superadmin/logs')}>
              <Monitor className="h-4 w-4 mr-2" />
              Application Logs
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/superadmin/pricing')}>
              <DollarSign className="h-4 w-4 mr-2" />
              Plans & Trials
            </Button>
          </div>
        </div>

        {/* Tenants Tab */}
        <TabsContent value="tenants">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Tenants
                </CardTitle>
                <CardDescription>
                  {loading
                    ? 'Loading tenants...'
                    : `${filteredTenants.length} of ${tenants.length} tenant${tenants.length !== 1 ? 's' : ''} shown`}
                </CardDescription>
              </div>
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-tenant-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Tenant
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create New Tenant</DialogTitle>
                    <DialogDescription>
                      Set up a new organization on the platform
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="shortname">Shortname *</Label>
                        <Input
                          id="shortname"
                          placeholder="calarts"
                          value={newTenant.shortname}
                          onChange={(e) => setNewTenant({ ...newTenant, shortname: e.target.value.toLowerCase() })}
                          data-testid="tenant-shortname-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="display_name">Display Name *</Label>
                        <Input
                          id="display_name"
                          placeholder="California Institute of the Arts"
                          value={newTenant.display_name}
                          onChange={(e) => setNewTenant({ ...newTenant, display_name: e.target.value })}
                          data-testid="tenant-display-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email_domains">Email Domain(s) *</Label>
                      <Input
                        id="email_domains"
                        placeholder="calarts.edu"
                        value={newTenant.email_domains}
                        onChange={(e) => setNewTenant({ ...newTenant, email_domains: e.target.value.toLowerCase() })}
                        data-testid="tenant-email-domains-input"
                      />
                      <p className="text-xs text-muted-foreground">Users with emails from this domain will be assigned to this tenant</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pricing_tier">Plan *</Label>
                      <Select
                        value={newTenant.pricing_tier}
                        onValueChange={(value) => setNewTenant({
                          ...newTenant,
                          pricing_tier: value,
                        })}
                      >
                        <SelectTrigger id="pricing_tier" data-testid="tenant-plan-select">
                          <SelectValue placeholder="Select a plan" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Trial">Trial</SelectItem>
                          {createPaidPlanOptions.map((plan) => (
                            <SelectItem key={plan.id || plan.name} value={plan.name}>
                              {plan.name}
                            </SelectItem>
                          ))}
                          <SelectItem value="Enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {createTenantPlanHelp}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="primary_color">Primary Color</Label>
                        <div className="flex gap-2">
                          <Input
                            id="primary_color"
                            type="color"
                            value={newTenant.primary_color}
                            onChange={(e) => setNewTenant({ ...newTenant, primary_color: e.target.value })}
                            className="w-12 h-10 p-1"
                          />
                          <Input
                            value={newTenant.primary_color}
                            onChange={(e) => setNewTenant({ ...newTenant, primary_color: e.target.value })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="secondary_color">Secondary Color</Label>
                        <div className="flex gap-2">
                          <Input
                            id="secondary_color"
                            type="color"
                            value={newTenant.secondary_color}
                            onChange={(e) => setNewTenant({ ...newTenant, secondary_color: e.target.value })}
                            className="w-12 h-10 p-1"
                          />
                          <Input
                            value={newTenant.secondary_color}
                            onChange={(e) => setNewTenant({ ...newTenant, secondary_color: e.target.value })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="initial_admin_email">Initial Admin Email (Optional)</Label>
                      <Input
                        id="initial_admin_email"
                        type="email"
                        placeholder="admin@calarts.edu"
                        value={newTenant.initial_admin_email}
                        onChange={(e) => setNewTenant({ ...newTenant, initial_admin_email: e.target.value.toLowerCase() })}
                        data-testid="tenant-admin-email-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        {createTenantWillBeInactive
                          ? 'This user will be invited with admin role and can complete payment from Billing & Subscription once they accept.'
                          : 'This user will be created with admin role'}
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateTenant} data-testid="confirm-create-tenant-btn">
                      Create Tenant
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4" data-testid="tenant-management-loading">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <Skeleton className="h-10 w-full lg:max-w-sm" />
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Skeleton className="h-10 w-full sm:w-[180px]" />
                      <Skeleton className="h-10 w-full sm:w-[180px]" />
                    </div>
                  </div>
                  <div className="rounded-lg border border-border">
                    <div className="grid grid-cols-5 gap-4 border-b border-border p-4">
                      {[1, 2, 3, 4, 5].map((header) => (
                        <Skeleton key={header} className="h-4 w-20" />
                      ))}
                    </div>
                    {[1, 2, 3, 4].map((row) => (
                      <div key={row} className="grid grid-cols-5 gap-4 border-b border-border p-4 last:border-b-0">
                        <div className="col-span-2 flex items-center gap-3">
                          <Skeleton className="h-10 w-10 rounded-lg" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-40" />
                            <Skeleton className="h-3 w-24" />
                          </div>
                        </div>
                        <Skeleton className="h-8 w-32" />
                        <Skeleton className="h-8 w-28" />
                        <Skeleton className="h-8 w-24 justify-self-end" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="relative w-full lg:max-w-sm">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={tenantSearch}
                        onChange={(event) => setTenantSearch(event.target.value)}
                        placeholder="Search tenants..."
                        className="pl-9"
                        data-testid="tenant-search-input"
                      />
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Select value={tenantPlanFilter} onValueChange={setTenantPlanFilter}>
                        <SelectTrigger className="w-full sm:w-[180px]">
                          <SelectValue placeholder="All plans" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All plans</SelectItem>
                          {planFilterOptions.map((plan) => (
                            <SelectItem key={plan} value={plan.toLowerCase()}>
                              {plan}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={tenantStatusFilter} onValueChange={setTenantStatusFilter}>
                        <SelectTrigger className="w-full sm:w-[180px]">
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All statuses</SelectItem>
                          {statusFilterOptions.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tenant</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Usage</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTenants.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="h-32 text-center text-muted-foreground">
                            No tenants match the current filters.
                          </TableCell>
                        </TableRow>
                      ) : pagedTenants.map((tenant) => {
                        const analytics = tenantUsageAnalytics.find(a => a.tenant_id === tenant.id) || {};
                        const documentCount = analytics.total_documents > 0
                          ? analytics.total_documents
                          : tenant.usage?.documents ?? tenant.document_count ?? 0;
                        const userCount = analytics.unique_eva_users > 0
                          ? analytics.unique_eva_users
                          : tenant.usage?.users ?? tenant.user_count ?? tenant.team_member_count ?? 0;
                        const conversationCount = analytics.total_sessions
                          ?? analytics.conversations_30d
                          ?? tenant.usage?.conversations_30d
                          ?? 0;
                        const engagementScore = analytics.engagement_score || 0;
                        const queryCount = analytics.retrievals_30d || tenant.usage?.queries_30d || 0;

                        return (
                          <TableRow
                            key={tenant.id}
                            data-testid={`tenant-row-${tenant.shortname}`}
                            className="cursor-pointer hover:bg-muted/40 transition-colors"
                            onClick={() => navigate(`/superadmin/tenants/${tenant.id}`)}
                          >
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <TenantIcon
                                  name={tenant.display_name}
                                  src={tenant.icon_url || tenant.logo_url}
                                  color={tenant.primary_color}
                                  className="w-8 h-8 rounded-lg"
                                  textClassName="font-semibold"
                                />
                                <div>
                                  <div className="font-medium">{tenant.display_name}</div>
                                  <div className="text-xs text-muted-foreground">{tenant.shortname}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium">{formatCellValue(getTenantPlanLabel(tenant))}</span>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {getTenantMonthlyEstimateLabel(tenant)}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <button
                                type="button"
                                className="flex flex-col items-start gap-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openLimitsDialog(tenant);
                                }}
                                title="View plan limits"
                              >
                                {(() => {
                                  const { badge, secondary } = getTenantStatusInfo(tenant);
                                  return (
                                    <>
                                      {badge}
                                      {secondary && <span className="text-xs text-muted-foreground">{secondary}</span>}
                                    </>
                                  );
                                })()}
                              </button>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1 text-sm">
                                <span className="flex items-center gap-1">
                                  <FileText className="h-3.5 w-3.5 text-muted-foreground" /> {documentCount} docs
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="h-3.5 w-3.5 text-muted-foreground" /> {userCount} users
                                </span>
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <BarChart3 className="h-3.5 w-3.5" /> {conversationCount} conversations
                                </span>
                                {(engagementScore > 0 || queryCount > 0) && (
                                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <TrendingUp className="h-3.5 w-3.5" />
                                    {engagementScore > 0 ? `${engagementScore} engagement` : `${queryCount} queries/30d`}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => navigate(`/superadmin/tenants/${tenant.id}`)}
                                  data-testid={`edit-tenant-${tenant.shortname}-btn`}
                                >
                                  <Settings className="h-4 w-4 mr-1" /> Edit
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewTenant(tenant.id)}
                                  data-testid={`view-tenant-${tenant.shortname}-btn`}
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      data-testid={`tenant-actions-${tenant.shortname}-btn`}
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => navigate(`/superadmin/tenants/${tenant.id}`)}>
                                      <Settings className="h-4 w-4 mr-2" />
                                      Open Editor
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate(`/superadmin/tenants/${tenant.id}?tab=access`)}>
                                      <FileText className="h-4 w-4 mr-2" />
                                      Access & Conversations
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openLimitsDialog(tenant)}>
                                      <Shield className="h-4 w-4 mr-2" />
                                      Limits
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate(`/superadmin/tenants/${tenant.id}?tab=token-usage`)}>
                                      <Coins className="h-4 w-4 mr-2" />
                                      AI Usage
                                    </DropdownMenuItem>
                                    {tenant.subscription_status === 'trial' && (
                                      <>
                                        <DropdownMenuItem onClick={() => handleExtendTrial(tenant.id, 14)}>
                                          <Calendar className="h-4 w-4 mr-2" />
                                          Extend Trial +14d
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => openConvertToPaidDialog(tenant)}>
                                          <CheckCircle className="h-4 w-4 mr-2 text-success" />
                                          Convert to Paid
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setSelectedTenant(tenant);
                                        setDeactivateDialogOpen(true);
                                      }}
                                    >
                                      {tenant.status === 'active' ? (
                                        <>
                                          <PowerOff className="h-4 w-4 mr-2 text-warning" />
                                          Deactivate
                                        </>
                                      ) : (
                                        <>
                                          <Power className="h-4 w-4 mr-2 text-success" />
                                          Activate
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive"
                                      onClick={() => {
                                        setSelectedTenant(tenant);
                                        setDeleteDialogOpen(true);
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {filteredTenants.length > 0 && (
                    <DataTablePagination
                      page={normalizedTenantPage}
                      totalPages={tenantTotalPages}
                      totalItems={filteredTenants.length}
                      pageSize={tenantPageSize}
                      pageSizeOptions={[10, 25, 50]}
                      onPageChange={setTenantPage}
                      onPageSizeChange={(size) => {
                        setTenantPageSize(size);
                        setTenantPage(1);
                      }}
                      itemLabel="tenants"
                      pageSizeTestId="tenant-page-size"
                    />
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Superadmins Tab */}
        <TabsContent value="superadmins">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Platform Superadmins
                </CardTitle>
                <CardDescription>
                  Users with access to all tenants
                </CardDescription>
              </div>
              <Dialog open={addSuperadminDialogOpen} onOpenChange={setAddSuperadminDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="add-superadmin-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Invite Superadmin
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Invite Superadmin</DialogTitle>
                    <DialogDescription>
                      Send an invitation to grant platform-wide superadmin access. The invitee will set their password upon accepting.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="sa_email">Email *</Label>
                      <Input
                        id="sa_email"
                        type="email"
                        placeholder="user@vistry.ai"
                        value={newSuperadmin.email}
                        onChange={(e) => setNewSuperadmin({ ...newSuperadmin, email: e.target.value })}
                        data-testid="superadmin-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sa_name">Name (optional)</Label>
                      <Input
                        id="sa_name"
                        placeholder="John Doe"
                        value={newSuperadmin.name}
                        onChange={(e) => setNewSuperadmin({ ...newSuperadmin, name: e.target.value })}
                        data-testid="superadmin-name-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAddSuperadminDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleAddSuperadmin} 
                      disabled={invitationLoading}
                      data-testid="confirm-add-superadmin-btn"
                    >
                      {invitationLoading ? 'Sending...' : 'Send Invitation'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Active Superadmins */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3">Active Superadmins</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {superadmins.map((sa) => (
                      <TableRow key={sa.id} data-testid={`superadmin-row-${sa.email}`}>
                        <TableCell className="font-medium">{formatCellValue(sa.name)}</TableCell>
                        <TableCell className="text-muted-foreground">{formatCellValue(sa.email)}</TableCell>
                        <TableCell className="text-center">
                          <Badge 
                            variant={sa.status === 'active' ? 'default' : 'secondary'}
                            className={sa.status === 'active' ? 'bg-success/10 text-success' : ''}
                          >
                            {formatCellValue(sa.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {(() => {
                            const isSelf = sa.email?.toLowerCase() === user?.email?.toLowerCase();
                            const isBreakGlass = sa.email?.toLowerCase() === BREAK_GLASS_SUPERADMIN_EMAIL;
                            const disableDestructive = isSelf || isBreakGlass;
                            return (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    data-testid={`superadmin-actions-${sa.email}-btn`}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {sa.status === 'active' ? (
                                    <DropdownMenuItem
                                      disabled={disableDestructive}
                                      onClick={() => handleDeactivateSuperadmin(sa.email)}
                                      data-testid={`deactivate-superadmin-${sa.email}-btn`}
                                    >
                                      <PowerOff className="h-4 w-4 mr-2 text-warning" />
                                      Deactivate
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem
                                      onClick={() => handleReactivateSuperadmin(sa.email)}
                                      data-testid={`reactivate-superadmin-${sa.email}-btn`}
                                    >
                                      <Power className="h-4 w-4 mr-2 text-success" />
                                      Reactivate
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    disabled={disableDestructive}
                                    onClick={() => handleHardDeleteSuperadmin(sa.email)}
                                    data-testid={`remove-superadmin-${sa.email}-btn`}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Remove
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                    {superadmins.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-4">
                          No superadmins found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pending Invitations */}
              {invitations.filter(inv => inv.status === 'pending').length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">Pending Invitations</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Invited By</TableHead>
                        <TableHead className="text-center">Expires</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invitations.filter(inv => inv.status === 'pending').map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{formatCellValue(inv.email)}</div>
                              {inv.name && <div className="text-xs text-muted-foreground">{inv.name}</div>}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{formatCellValue(inv.invited_by)}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="bg-warning/10 text-warning">
                              {formatTimeAgo(inv.expires_at)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResendInvitation(inv.id)}
                                title="Resend invitation"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleCancelInvitation(inv.id)}
                                title="Cancel invitation"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Passkey enrollment moved to /settings/security (EIP-305). It cannot
              live behind SuperadminRoute: the enrollment gate blocks superadmin
              pages until enrollment is complete, so hosting it here deadlocks.
              Two live mounts would also race each other on /passkey/status. */}
          <div className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Fingerprint className="w-5 h-5" />
                  Passkeys
                </CardTitle>
                <CardDescription>
                  Manage the passkeys used to sign in to your superadmin account.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" onClick={() => navigate('/settings/security')}>
                  Manage passkeys
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Access Requests Tab */}
        <TabsContent value="access-requests">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Access Requests
              </CardTitle>
              <CardDescription>
                Review and manage access requests from users wanting to join existing organizations
              </CardDescription>
            </CardHeader>
            <CardContent>
              {accessRequests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <UserPlus className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No access requests</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {accessRequests.map((req) => (
                    <div 
                      key={req.id} 
                      className={`border rounded-lg p-4 ${req.status === 'pending' ? 'bg-warning/5 border-warning/30' : 'bg-muted/30'}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* User Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-medium">{req.first_name} {req.last_name}</span>
                            <Badge 
                              variant={
                                req.status === 'pending' ? 'warning' : 
                                req.status === 'approved' ? 'success' : 
                                'destructive'
                              }
                              className="text-xs"
                            >
                              {req.status}
                            </Badge>
                            {req.tenant_id === '__NEW_TENANT__' && (
                              <Badge 
                                variant="outline"
                                className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                              >
                                <Building2 className="h-3 w-3 mr-1" />
                                New Tenant
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{req.email}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span>{req.tenant_name || 'New Organization'}</span>
                            <span>•</span>
                            <span>{new Date(req.created_at).toLocaleDateString()}</span>
                          </div>
                          {req.message && (
                            <p className="text-sm text-muted-foreground mt-2 italic">"{req.message}"</p>
                          )}
                        </div>
                        
                        {/* Actions */}
                        {req.status === 'pending' ? (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Select
                              value={selectedRole}
                              onValueChange={setSelectedRole}
                            >
                              <SelectTrigger className="w-24 h-8 text-xs">
                                <SelectValue placeholder="Role" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="viewer">Viewer</SelectItem>
                                <SelectItem value="analyst">Analyst</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8"
                              disabled={reviewingRequest === req.id}
                              onClick={() => handleReviewAccessRequest(req.id, 'approve')}
                              data-testid={`approve-request-${req.id}`}
                            >
                              <CheckCircle className="h-3.5 w-3.5 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8"
                              disabled={reviewingRequest === req.id}
                              onClick={() => handleReviewAccessRequest(req.id, 'decline')}
                              data-testid={`decline-request-${req.id}`}
                            >
                              <XCircle className="h-3.5 w-3.5 mr-1" />
                              Decline
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {req.reviewed_by && `Reviewed by ${req.reviewed_by}`}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== SIGNUP ACTIVITY TAB ===== */}
        <TabsContent value="signup-activity" data-testid="signup-activity-content">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Pending Signups
                </CardTitle>
                <CardDescription>
                  Signups awaiting email verification or admin approval — sorted oldest first
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingSignups.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No pending signups</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Campaign</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingSignups.map((p) => (
                        <TableRow key={p.id} data-testid={`pending-signup-${p.id}`}>
                          <TableCell className="font-medium">{p.email}</TableCell>
                          <TableCell>{p.company_name || p.company_domain || '—'}</TableCell>
                          <TableCell>
                            <Badge variant={p.email_verified ? 'warning' : 'destructive'}>
                              {p.email_verified ? 'Awaiting approval' : 'Awaiting email verification'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {p.utm_campaign || p.utm_source || '—'}
                            {p.landing_host && <span className="block">{p.landing_host}</span>}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(p.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            {!p.email_verified && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={resendingEmail === p.email}
                                onClick={() => handleResendVerification(p.email)}
                                data-testid={`resend-verification-${p.id}`}
                              >
                                {resendingEmail === p.email ? (
                                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                ) : (
                                  <Mail className="h-3.5 w-3.5 mr-1" />
                                )}
                                Resend
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5" />
                      Signup Activity
                    </CardTitle>
                    <CardDescription>
                      Attribution and lifecycle events across the signup funnel
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={exportSignupActivityCsv} data-testid="export-signup-activity-csv">
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Input
                    placeholder="Filter by event name (e.g. signup_completed)"
                    value={activityEventFilter}
                    onChange={(e) => setActivityEventFilter(e.target.value)}
                    className="max-w-xs"
                    data-testid="signup-activity-event-filter"
                  />
                  <Input
                    placeholder="Filter by UTM campaign"
                    value={activityCampaignFilter}
                    onChange={(e) => setActivityCampaignFilter(e.target.value)}
                    className="max-w-xs"
                    data-testid="signup-activity-campaign-filter"
                  />
                  <Button
                    size="sm"
                    onClick={() => loadSignupActivity({
                      event_name: activityEventFilter || undefined,
                      utm_campaign: activityCampaignFilter || undefined,
                    })}
                    data-testid="signup-activity-apply-filters"
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Apply
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {signupActivity.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No signup activity recorded yet</p>
                  </div>
                ) : (
                  <Table data-testid="signup-activity-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Event</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Email / Visitor</TableHead>
                        <TableHead>Tenant</TableHead>
                        <TableHead>Campaign</TableHead>
                        <TableHead>Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {signupActivity.map((e) => (
                        <TableRow key={e.id} data-testid={`signup-activity-${e.id}`}>
                          <TableCell className="font-medium">{e.event_name}</TableCell>
                          <TableCell>
                            <Badge variant={e.event_status === 'error' ? 'destructive' : 'outline'}>
                              {e.event_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs">{e.email || e.visitor_id || '—'}</TableCell>
                          <TableCell className="text-xs">
                            {e.tenant_display_name || (e.tenant_id === 'platform' ? '—' : e.tenant_id) || '—'}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {e.utm_campaign || e.landing_host || '—'}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(e.created_at).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== CONNECTOR DEMAND TAB ===== */}
        <TabsContent value="connector-demand" data-testid="connector-demand-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Connector Integration Demand
              </CardTitle>
              <CardDescription>
                Global votes from all users across tenants. Top requested integrations to prioritize.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {connectorVotes.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No votes yet. Users can vote on connectors from the Connectors page.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {[...connectorVotes].sort((a, b) => b.vote_count - a.vote_count).map((item, idx) => (
                    <div key={item.connector_name} className="flex items-center gap-4 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <span className="text-lg font-bold text-muted-foreground w-6">#{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.connector_name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${Math.min(100, (item.vote_count / (connectorVotes[0]?.vote_count || 1)) * 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold w-8 text-right">{item.vote_count}</span>
                        <span className="text-xs text-muted-foreground">votes</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== DEFAULTS TAB ===== */}
        <TabsContent value="defaults" data-testid="defaults-content">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-primary" />
                  Default Settings for New Tenants
                </CardTitle>
                <CardDescription>
                  These settings are applied to every new tenant created. Individual tenants can be adjusted in the Tenant Editor.
                </CardDescription>
              </CardHeader>
              {defaultSettings ? (
                <CardContent className="space-y-6">
                  {/* AI Model Defaults */}
                  <div>
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Zap className="h-4 w-4 text-purple-500" /> Eva Model Defaults
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Standard Model</Label>
                        <Select
                          value={defaultSettings.fast_model}
                          onValueChange={v => setDefaultSettings(d => ({ ...d, fast_model: v }))}
                        >
                          <SelectTrigger data-testid="default-fast-model">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="claude-sonnet-4-6">Eva Smart</SelectItem>
                            <SelectItem value="claude-haiku-4-5-20251001">Eva Fast</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Advanced Model</Label>
                        <Select
                          value={defaultSettings.thinking_model}
                          onValueChange={v => setDefaultSettings(d => ({ ...d, thinking_model: v }))}
                        >
                          <SelectTrigger data-testid="default-thinking-model">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="claude-opus-4-6">Eva Deep</SelectItem>
                            <SelectItem value="claude-sonnet-4-6">Eva Smart</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Session Defaults */}
                  <div className="border-t pt-4">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-info" /> Session Defaults
                    </h3>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Default Timeout (minutes)</Label>
                      <Input
                        type="number"
                        value={defaultSettings.default_session_timeout_minutes}
                        onChange={e => setDefaultSettings(d => ({ ...d, default_session_timeout_minutes: parseInt(e.target.value) || 10 }))}
                        className="max-w-xs"
                        data-testid="default-session-timeout"
                      />
                    </div>
                  </div>

                  {/* Topic Classification Defaults */}
                  <div className="border-t pt-4">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Tags className="h-4 w-4 text-warning" /> Topic Classification (Default)
                    </h3>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Max Topics per Conversation</Label>
                          <Input
                            type="number" min={1} max={10}
                            value={defaultSettings.topic_classification_config?.max_topics ?? 3}
                            onChange={e => setDefaultSettings(d => ({
                              ...d,
                              topic_classification_config: {
                                ...(d.topic_classification_config || {}),
                                max_topics: parseInt(e.target.value) || 3,
                              }
                            }))}
                            data-testid="default-max-topics"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Max Sub-Topics per Topic</Label>
                          <Input
                            type="number" min={1} max={5}
                            value={defaultSettings.topic_classification_config?.max_sub_topics_per_topic ?? 2}
                            onChange={e => setDefaultSettings(d => ({
                              ...d,
                              topic_classification_config: {
                                ...(d.topic_classification_config || {}),
                                max_sub_topics_per_topic: parseInt(e.target.value) || 2,
                              }
                            }))}
                            data-testid="default-max-sub-topics"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <Label className="text-xs font-medium">Allow Emerging Topics</Label>
                          <p className="text-xs text-muted-foreground">Suggest topics not in the existing taxonomy</p>
                        </div>
                        <Switch
                          checked={defaultSettings.topic_classification_config?.allow_emerging_topics ?? true}
                          onCheckedChange={v => setDefaultSettings(d => ({
                            ...d,
                            topic_classification_config: {
                              ...(d.topic_classification_config || {}),
                              allow_emerging_topics: v,
                            }
                          }))}
                          data-testid="default-allow-emerging-topics"
                        />
                      </div>
                    </div>
                  </div>

                  {/* File Type Defaults */}
                  <div className="border-t pt-4">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-success" /> Allowed File Types (Default)
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {/* Keep in sync with FILE_TYPES in TenantEditorPage.js. */}
                      {['pdf', 'xlsx', 'docx', 'pptx', 'png'].map(ext => {
                        const enabled = (defaultSettings.allowed_file_types || ['pdf']).includes(ext);
                        return (
                          <button
                            key={ext}
                            onClick={() => {
                              const cur = defaultSettings.allowed_file_types || ['pdf'];
                              const updated = enabled ? cur.filter(t => t !== ext) : [...cur, ext];
                              if (updated.length === 0) return;
                              setDefaultSettings(d => ({ ...d, allowed_file_types: updated }));
                            }}
                            className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all cursor-pointer ${
                              enabled ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-border hover:border-primary/50'
                            }`}
                            data-testid={`default-filetype-${ext}`}
                          >
                            {ext.toUpperCase()}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* UX Feature Defaults */}
                  <div className="border-t pt-4">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-500" /> UX Features (Default)
                    </h3>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <Label className="text-xs font-medium">Show Welcome Interstitial</Label>
                        <p className="text-xs text-muted-foreground">Display a welcome screen for new users on their first visit</p>
                      </div>
                      <Switch
                        checked={defaultSettings.show_welcome_interstitial ?? true}
                        onCheckedChange={v => setDefaultSettings(d => ({ ...d, show_welcome_interstitial: v }))}
                        data-testid="default-show-welcome-interstitial"
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <Label className="text-xs font-medium">Smart Eva Pulse</Label>
                        <p className="text-xs text-muted-foreground">Show the conversational Eva Pulse assistant on Overview instead of the standard action list</p>
                      </div>
                      <Switch
                        checked={defaultSettings.smart_eva_pulse_enabled ?? false}
                        onCheckedChange={v => setDefaultSettings(d => ({ ...d, smart_eva_pulse_enabled: v }))}
                        data-testid="default-smart-eva-pulse"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-2 border-t">
                    <Button onClick={saveDefaultSettings} disabled={savingDefaults} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-defaults-btn">
                      {savingDefaults ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Defaults
                    </Button>
                  </div>
                </CardContent>
              ) : (
                <CardContent>
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      <Dialog open={analyticsDialogOpen} onOpenChange={setAnalyticsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              {selectedTenant?.display_name} - Analytics
            </DialogTitle>
          </DialogHeader>
          {analyticsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : selectedTenantAnalytics ? (
            <div className="space-y-6">
              {/* Subscription Status */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Status</p>
                    <p className="text-lg font-semibold capitalize">{selectedTenantAnalytics.subscription_status}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Days Remaining</p>
                    <p className="text-lg font-semibold">{selectedTenantAnalytics.limits?.days_remaining || 'N/A'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Est. Monthly</p>
                    <p className="text-lg font-semibold">${selectedTenantAnalytics.billing_estimate?.monthly_total || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">Plan</p>
                    <p className="text-lg font-semibold">{selectedTenantAnalytics.billing_estimate?.tier_name || 'N/A'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Usage vs Limits */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Usage vs Limits</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Documents</span>
                      <span>{selectedTenantAnalytics.current_usage?.document_count ?? 0} / {formatCellValue(selectedTenantAnalytics.trial_config?.max_documents)}</span>
                    </div>
                    <Progress 
                      value={(selectedTenantAnalytics.current_usage?.document_count ?? 0) / (selectedTenantAnalytics.trial_config?.max_documents ?? 1) * 100} 
                      className="h-2"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Users</span>
                      <span>{selectedTenantAnalytics.current_usage?.team_member_count ?? 0} / {formatCellValue(selectedTenantAnalytics.trial_config?.max_users)}</span>
                    </div>
                    <Progress 
                      value={(selectedTenantAnalytics.current_usage?.team_member_count ?? 0) / (selectedTenantAnalytics.trial_config?.max_users ?? 1) * 100} 
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Usage Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Current Usage</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <div className="text-center p-3 bg-secondary/50 rounded-lg">
                      <p className="text-2xl font-bold">{selectedTenantAnalytics.current_usage?.document_count || 0}</p>
                      <p className="text-xs text-muted-foreground">Documents</p>
                    </div>
                    <div className="text-center p-3 bg-secondary/50 rounded-lg">
                      <p className="text-2xl font-bold">{selectedTenantAnalytics.current_usage?.chunk_count || 0}</p>
                      <p className="text-xs text-muted-foreground">Pages</p>
                    </div>
                    <div className="text-center p-3 bg-secondary/50 rounded-lg">
                      <p className="text-2xl font-bold">{selectedTenantAnalytics.current_usage?.user_count || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Users</p>
                    </div>
                    <div className="text-center p-3 bg-secondary/50 rounded-lg">
                      <p className="text-2xl font-bold">{selectedTenantAnalytics.current_usage?.conversation_count_30d || 0}</p>
                      <p className="text-xs text-muted-foreground">Conversations (30d)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => {
                  setAnalyticsDialogOpen(false);
                  navigate(`/superadmin/tenants/${selectedTenant?.id}?tab=token-usage`);
                }}>
                  <Coins className="h-4 w-4 mr-2" />
                  AI Usage
                </Button>
                {selectedTenantAnalytics.subscription_status === 'trial' && (
                  <>
                    <Button variant="outline" onClick={() => handleExtendTrial(selectedTenant?.id, 14)}>
                      <Calendar className="h-4 w-4 mr-2" />
                      Extend +14 Days
                    </Button>
                    <Button onClick={() => openConvertToPaidDialog(selectedTenant)}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Convert to Paid
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <p className="text-center py-8 text-muted-foreground">No analytics data available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Trial Configuration Dialog */}
      <Dialog open={trialDialogOpen} onOpenChange={setTrialDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configure Trial - {selectedTenant?.display_name}</DialogTitle>
            <DialogDescription>
              Apply trial limits and AI model configuration for this tenant. Saving switches this tenant to trial entitlements.
            </DialogDescription>
          </DialogHeader>
          {trialConfig ? (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Duration (days)</Label>
                <Input
                  type="number"
                  value={trialConfig.duration_days}
                  onChange={(e) => setTrialConfig({ ...trialConfig, duration_days: parseInt(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Documents</Label>
                <Input
                  type="number"
                  value={trialConfig.max_documents}
                  onChange={(e) => setTrialConfig({ ...trialConfig, max_documents: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Max Pages/Doc</Label>
                <Input
                  type="number"
                  value={trialConfig.max_chunks_per_document}
                  onChange={(e) => setTrialConfig({ ...trialConfig, max_chunks_per_document: parseInt(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Users</Label>
                <Input
                  type="number"
                  value={trialConfig.max_users}
                  onChange={(e) => setTrialConfig({ ...trialConfig, max_users: parseInt(e.target.value) })}
                />
              </div>
            </div>
            
            {/* AI Model Configuration */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-purple-500" />
                Eva Model Configuration
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Standard Model</Label>
                  <select
                    value={trialConfig.fast_model}
                    onChange={(e) => setTrialConfig({ ...trialConfig, fast_model: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    {MODEL_OPTIONS.fast.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">Used for quick responses</p>
                </div>
                <div className="space-y-2">
                  <Label>Advanced Model</Label>
                  <select
                    value={trialConfig.thinking_model}
                    onChange={(e) => setTrialConfig({ ...trialConfig, thinking_model: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    {MODEL_OPTIONS.thinking.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">Used for complex queries</p>
                </div>
              </div>
            </div>
          </div>
          ) : (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrialDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateTrial} disabled={!trialConfig}>Save Configuration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tenant Limits Dialog */}
      <Dialog open={limitsDialogOpen} onOpenChange={setLimitsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Tenant Limits - {selectedTenant?.display_name}
            </DialogTitle>
            <DialogDescription>
              Set tenant-specific limits for Enterprise customers and trial exceptions. Blank fields use the current plan or trial default.
            </DialogDescription>
          </DialogHeader>

          {limitsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : tenantLimits ? (
            <div className="space-y-6 py-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">Status</p>
                  <p className="font-semibold capitalize">{tenantLimits.subscription_status}</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">Plan</p>
                  <p className="font-semibold">{tenantLimits.pricing_tier || 'Trial'}</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">Overrides</p>
                  <p className="font-semibold">{Object.keys(tenantLimits.overrides || {}).length}</p>
                </div>
              </div>

              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Limit</TableHead>
                      <TableHead>Effective</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead className="w-[220px]">Override</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(tenantLimits.limit_details || limitFields).map((limit) => {
                      const key = limit.key;
                      const label = limit.label;
                      return (
                      <TableRow key={key}>
                        <TableCell className="font-medium">{label}</TableCell>
	                        <TableCell>{limit.effective_limit !== undefined ? formatLimit(limit.effective_limit) : formatLimit(tenantLimits.effective_limits?.[key])}</TableCell>
                        <TableCell>
                          <Badge variant={tenantLimits.sources?.[key] === 'not_configured' || tenantLimits.sources?.[key] === 'custom_not_configured' ? 'destructive' : 'outline'}>
                            {limit.source_label || formatLimitSource(tenantLimits.sources?.[key])}
                          </Badge>
                        </TableCell>
                        <TableCell>
	                          <Input
	                            type="number"
	                            min="0"
	                            step={limit.type === 'decimal' ? '0.01' : '1'}
	                            value={limitDraft[key] ?? ''}
	                            placeholder="Use default"
                            onChange={(e) => setLimitDraft((draft) => ({ ...draft, [key]: e.target.value }))}
                          />
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
                Enterprise tenants should have explicit numeric overrides for the limits you want enforced. Use blank fields only when the plan default should apply.
              </div>
            </div>
          ) : (
            <p className="text-center py-8 text-muted-foreground">No limit data available</p>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                const cleared = {};
                limitFields.forEach(({ key }) => { cleared[key] = ''; });
                setLimitDraft(cleared);
              }}
              disabled={savingLimits || limitsLoading}
            >
              Use Defaults
            </Button>
            <Button variant="outline" onClick={() => setLimitsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveLimits} disabled={savingLimits || limitsLoading}>
              {savingLimits ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Limits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <AlertDialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedTenant?.status === 'active' ? 'Deactivate' : 'Activate'} Tenant
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedTenant?.status === 'active' 
                ? `This will prevent all users in "${selectedTenant?.display_name}" from logging in. Data will be preserved.`
                : `This will allow users in "${selectedTenant?.display_name}" to log in again.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedTenant(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeactivateTenant}
              className={selectedTenant?.status === 'active' ? 'bg-warning hover:bg-warning/90' : 'bg-success hover:bg-success/90'}
            >
              {selectedTenant?.status === 'active' ? 'Deactivate' : 'Activate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tenant</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{selectedTenant?.display_name}&quot; and all associated data including documents, topics, and settings. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedTenant(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteTenant}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Convert to Paid Dialog */}
      <Dialog open={convertToPaidDialogOpen} onOpenChange={setConvertToPaidDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Convert to Paid - {selectedTenant?.display_name}</DialogTitle>
            <DialogDescription>
              Choose how to convert this tenant from trial to paid subscription
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Conversion Method */}
            <div className="space-y-3">
              <Label>Conversion Method</Label>
              <RadioGroup value={convertMethod} onValueChange={setConvertMethod} className="space-y-3">
                <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                  <RadioGroupItem value="already_paid" id="already_paid" className="mt-1" />
                  <Label htmlFor="already_paid" className="cursor-pointer flex-1">
                    <div className="font-medium">Already Paid</div>
                    <div className="text-sm text-muted-foreground">
                      Customer has already paid (offline payment, wire transfer, etc.). Activate immediately.
                    </div>
                  </Label>
                </div>
                <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                  <RadioGroupItem value="generate_invoice" id="generate_invoice" className="mt-1" />
                  <Label htmlFor="generate_invoice" className="cursor-pointer flex-1">
                    <div className="font-medium">Generate Invoice</div>
                    <div className="text-sm text-muted-foreground">
                      Create and send invoice via QuickBooks. Customer will receive payment link via email.
                    </div>
                  </Label>
                </div>
                <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                  <RadioGroupItem value="manual" id="manual" className="mt-1" />
                  <Label htmlFor="manual" className="cursor-pointer flex-1">
                    <div className="font-medium">Manual Entry</div>
                    <div className="text-sm text-muted-foreground">
                      Mark as paid and handle billing outside the system (enterprise deals, custom contracts).
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Billing Period (for invoice) */}
            {convertMethod === 'generate_invoice' && (
              <div className="space-y-3">
                <Label>Billing Period</Label>
                <RadioGroup value={billingPeriod} onValueChange={setBillingPeriod} className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="monthly" id="monthly" />
                    <Label htmlFor="monthly">Monthly</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="annual" id="annual" />
                    <Label htmlFor="annual">Annual (10% discount)</Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Pricing Summary */}
            {selectedTenantAnalytics && (
              <div className="p-4 bg-secondary/50 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Users</span>
                  <span>{selectedTenantAnalytics.billing_estimate?.user_count ?? selectedTenantAnalytics.current_usage?.user_count ?? 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Estimated Monthly</span>
                  <span className="font-medium">
                    {selectedTenantAnalytics.billing_estimate?.monthly_total != null
                      ? `$${Number(selectedTenantAnalytics.billing_estimate.monthly_total).toLocaleString()}`
                      : 'Contact sales'}
                  </span>
                </div>
                {convertMethod === 'generate_invoice' && billingPeriod === 'annual' && (
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span>Annual Discount</span>
                    <span>-10%</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertToPaidDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConvertToPaid} 
              disabled={convertLoading}
              data-testid="confirm-convert-btn"
            >
              {convertLoading ? (
                <>Processing...</>
              ) : convertMethod === 'generate_invoice' ? (
                <>Generate Invoice</>
              ) : (
                <>Activate Subscription</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Active Sessions Dialog */}
      <Dialog open={sessionsDialogOpen} onOpenChange={setSessionsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Active Sessions - {selectedTenant?.display_name}
            </DialogTitle>
            <DialogDescription>
              View and manage logged-in users. Revoking a session will force the user to log in again.
            </DialogDescription>
          </DialogHeader>
          
          {sessionsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : tenantSessions.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No active sessions</p>
              <p className="text-sm text-muted-foreground mt-1">
                Users will appear here when they log in
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                <span className="text-sm">
                  <span className="font-medium">{tenantSessions.length}</span> active session{tenantSessions.length !== 1 ? 's' : ''}
                </span>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleRevokeAllSessions}
                  data-testid="revoke-all-sessions-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Log Out All
                </Button>
              </div>
              
              {/* Sessions List */}
              <div className="space-y-2">
                {tenantSessions.map((session) => (
                  <div 
                    key={session.id} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50"
                    data-testid={`session-${session.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      {session.avatar_url ? (
                        <img 
                          src={session.avatar_url} 
                          alt={session.user_name}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">
                            {(session.user_name || session.user_email || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      
                      {/* User info */}
                      <div>
                        <div className="font-medium">{session.user_name || session.user_email}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          <span>{session.user_email}</span>
                          {session.role && (
                            <Badge variant="outline" className="text-xs">
                              {session.role}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Device & Activity */}
                      <div className="text-right text-xs text-muted-foreground">
                        <div className="flex items-center gap-1 justify-end">
                          {session.device_info?.toLowerCase().includes('mobile') ? (
                            <Smartphone className="h-3 w-3" />
                          ) : (
                            <Monitor className="h-3 w-3" />
                          )}
                          <span>{formatCellValue(session.device_info)}</span>
                        </div>
                        <div className="flex items-center gap-1 justify-end mt-0.5">
                          <Clock className="h-3 w-3" />
                          <span>Active {formatTimeAgo(session.last_activity)}</span>
                        </div>
                      </div>
                      
                      {/* Revoke button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleRevokeSession(session.id)}
                        disabled={revokingSession === session.id}
                        data-testid={`revoke-session-${session.id}-btn`}
                      >
                        {revokingSession === session.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-destructive" />
                        ) : (
                          <LogOut className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSessionsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Token Usage & Plan Dialog */}
      <Dialog open={tokenUsageDialogOpen} onOpenChange={setTokenUsageDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-primary" />
              AI Usage & Plan - {selectedTenant?.display_name}
            </DialogTitle>
            <DialogDescription>
              Manage AI usage limits and pricing plan for this tenant
            </DialogDescription>
          </DialogHeader>
          
          {tokenUsageLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : tenantTokenUsage ? (
            <div className="space-y-6">
              {/* Current Plan & Usage Summary */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Current Plan</span>
                    </div>
                    <Badge className={getPlanBadgeColor(tenantTokenUsage.plan?.type)}>
                      {tenantTokenUsage.plan?.display_name || 'Trial'}
                    </Badge>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Usage</span>
                    </div>
                    <p className="text-lg font-semibold">
                      {formatTokens(tenantTokenUsage.current_period?.tokens_used)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Cost (Period)</span>
                    </div>
                    <p className="text-lg font-semibold">
                      {formatCost(tenantTokenUsage.current_period?.cost_usd)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Usage %</span>
                    </div>
                    <p className={`text-lg font-semibold ${
                      tenantTokenUsage.current_period?.usage_percentage > 100 ? 'text-destructive' :
                      tenantTokenUsage.current_period?.usage_percentage > 80 ? 'text-warning' :
                      'text-success'
                    }`}>
                      {tenantTokenUsage.current_period?.usage_percentage?.toFixed(1) || 0}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Token Usage Progress */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    AI Usage vs Limit
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Monthly Usage</span>
                      <span>
                        {formatTokens(tenantTokenUsage.current_period?.tokens_used)} / {formatTokens(tenantTokenUsage.plan?.token_limit)}
                      </span>
                    </div>
                    <Progress 
                      value={Math.min(tenantTokenUsage.current_period?.usage_percentage || 0, 100)}
                      className={`h-3 ${
                        tenantTokenUsage.current_period?.usage_percentage > 100 ? '[&>div]:bg-destructive' :
                        tenantTokenUsage.current_period?.usage_percentage > 80 ? '[&>div]:bg-warning' :
                        ''
                      }`}
                    />
                    {tenantTokenUsage.current_period?.usage_percentage > 100 && (
                      <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Limit exceeded - API calls may be blocked
                      </p>
                    )}
                    {tenantTokenUsage.current_period?.usage_percentage > 80 && tenantTokenUsage.current_period?.usage_percentage <= 100 && (
                      <p className="text-xs text-warning mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Approaching limit - consider upgrading plan
                      </p>
                    )}
                  </div>
                  {tenantTokenUsage.plan?.cost_limit && (
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span>Cost Limit</span>
                        <span>
                          {formatCost(tenantTokenUsage.current_period?.cost_usd)} / ${tenantTokenUsage.plan?.cost_limit}
                        </span>
                      </div>
                      <Progress 
                        value={Math.min((tenantTokenUsage.current_period?.cost_usd / tenantTokenUsage.plan?.cost_limit) * 100, 100)}
                        className="h-2"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Plan Management */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Plan Management
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-end gap-4">
                    <div className="flex-1 space-y-2">
                      <Label>Change Plan</Label>
                      <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                        <SelectTrigger data-testid="plan-select">
                          <SelectValue placeholder="Select plan" />
                        </SelectTrigger>
                        <SelectContent>
                          {availablePlans.map((plan) => (
                            <SelectItem key={plan.type} value={plan.type}>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{plan.display_name}</span>
                                <span className="text-muted-foreground text-xs">
                                  ({formatTokens(plan.token_limit)} usage/mo)
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={handleUpdatePlan} 
                      disabled={updatingPlan || selectedPlan === tenantTokenUsage.plan?.type}
                      data-testid="update-plan-btn"
                    >
                      {updatingPlan ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        'Update Plan'
                      )}
                    </Button>
                  </div>

                  {/* Plan Details */}
                  {selectedPlan && (
                    <div className="p-3 bg-secondary/50 rounded-lg space-y-2 text-sm">
                      {availablePlans.find(p => p.type === selectedPlan) && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Usage Limit</span>
                            <span className="font-medium">
                              {formatTokens(availablePlans.find(p => p.type === selectedPlan)?.token_limit)}/month
                            </span>
                          </div>
	                          <div className="flex justify-between">
	                            <span className="text-muted-foreground">Cost Limit</span>
	                            <span className="font-medium">
	                              {availablePlans.find(p => p.type === selectedPlan)?.cost_limit
	                                ? `$${availablePlans.find(p => p.type === selectedPlan)?.cost_limit}`
	                                : 'Not configured'}
	                            </span>
	                          </div>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Usage Breakdown */}
              {tenantTokenUsage.breakdown && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Usage Breakdown (Current Period)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* By Operation */}
                      {tenantTokenUsage.breakdown.by_operation?.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-muted-foreground">By Operation</p>
                          {tenantTokenUsage.breakdown.by_operation.map((op) => (
                            <div key={op.operation} className="flex justify-between items-center p-2 bg-secondary/30 rounded">
                              <span className="capitalize">{op.operation}</span>
                              <div className="text-right text-sm">
                                <span className="font-medium">{formatTokens(op.tokens)}</span>
                                <span className="text-muted-foreground ml-2">({op.requests} req)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* By Model */}
                      {tenantTokenUsage.breakdown.by_model?.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-muted-foreground">By Model</p>
                          {tenantTokenUsage.breakdown.by_model.map((model) => (
                            <div key={model.model} className="flex justify-between items-center p-2 bg-secondary/30 rounded">
                              <span className="text-sm truncate">{model.model}</span>
                              <div className="text-right text-sm">
                                <span className="font-medium">{formatTokens(model.tokens)}</span>
                                <span className="text-muted-foreground ml-2">{formatCost(model.cost)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quick Actions */}
              <div className="flex justify-between items-center pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetUsage}
                  disabled={resettingUsage}
                  className="text-destructive hover:text-destructive"
                  data-testid="reset-usage-btn"
                >
                  {resettingUsage ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reset Usage Counters
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Period resets: {tenantTokenUsage.current_period?.period_end 
                    ? new Date(tenantTokenUsage.current_period.period_end).toLocaleDateString()
                    : 'Monthly'}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Coins className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No usage data available</p>
              <p className="text-sm text-muted-foreground mt-1">
                Token tracking will begin when the tenant makes API calls
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTokenUsageDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
